export interface Tab {
  id: string;
  label: string;
  badge?: number | string;
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  /** Optional content rendered right-aligned on the same row as the tab buttons. */
  rightSlot?: React.ReactNode;
}

/**
 * Horizontal tab bar — quiet underline style, accent on the active tab.
 */
export function Tabs({
  tabs,
  activeId,
  onChange,
  className = "",
  rightSlot,
}: TabsProps) {
  return (
    <div
      role="tablist"
      className={`flex items-center gap-1 border-b border-line ${className}`}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        const isDisabled = tab.disabled;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(tab.id)}
            className={`
              relative flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors
              ${
                isActive
                  ? "text-ink"
                  : isDisabled
                    ? "cursor-not-allowed text-ink-3/60"
                    : "text-ink-3 hover:text-ink"
              }
            `}
          >
            <span>{tab.label}</span>
            {tab.badge !== undefined && tab.badge !== null && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] ${
                  isActive
                    ? "bg-accent-soft text-accent"
                    : "bg-surface-3 text-ink-3"
                }`}
              >
                {tab.badge}
              </span>
            )}
            {isActive && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-accent" />
            )}
          </button>
        );
      })}
      {rightSlot && <div className="ml-auto flex items-center gap-2 pr-2">{rightSlot}</div>}
    </div>
  );
}
